/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import type {
  BrandShelfAcesResponseModel,
  FacetModel,
  GetBrandListResultsUsingGetParamsModel,
  SkuRecordModel,
} from '@/api/types/browse-search-types';
import { getAxios } from '@/lib/axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { createQuery } from '@/utils/createReactQuery';
import { useLocale } from '@/hooks/useLocale';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useRouter } from 'next/router';
import {
  type InfiniteData,
  type QueryClient,
  type QueryStatus,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { parseUrl } from '@/utils/urlHelpers';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useIs24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import type { GetProductBrandDataOptionsAces, MappedBrandShelfResultsAces } from '../interface';
import { useMemo } from 'react';
import { getKiboDecisionFlag, useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getMapFacet, useMapFacet } from '@/hooks/useMapFacet';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';

const ACES_PART_TYPES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v1/brand-shelves`;

const selector = (
  { brandShelfResults, redirectUrl, xFusionQueryId }: BrandShelfAcesResponseModel,
  mapFacet: ReturnType<typeof useMapFacet>
): MappedBrandShelfResultsAces => {
  return {
    redirectLocation: redirectUrl,
    recordsPerPage: brandShelfResults?.recordsPerPage ?? 12,
    total: brandShelfResults?.totalNumberOfRecords,
    quickFilters: brandShelfResults?.quickFilters,
    firstRecNum: brandShelfResults?.firstRecordNumber,
    lastRecNum: brandShelfResults?.lastRecordNumber,
    staticNavigation: brandShelfResults?.facets?.map(mapFacet) ?? [],
    brandName: brandShelfResults?.brandName ?? '',
    // Vehicle information from the response
    year: brandShelfResults?.year,
    makeId: brandShelfResults?.makeId,
    modelId: brandShelfResults?.modelId,
    makeName: brandShelfResults?.makeName,
    modelName: brandShelfResults?.modelName,
    subModelName: brandShelfResults?.subModelName,
    subModelId: brandShelfResults?.subModelId,
    unitOfMeasureMatch: brandShelfResults?.unitOfMeasureMatch,
    records:
      brandShelfResults?.skuRecords?.map((record) => ({
        ...record,
        active: record.activeFlag,
        alternatePartNumber: record.alternatePartNumber,
        brandName: record.brandName,
        brandId: record.brandId,
        seoUrl: record.productDetailsPageUrl,
        imageUrl: record.productImageUrl ?? '',
        productImageUrl: record.productImageUrl ?? '',
        description: record.itemDescription,
        skuNumber: Number(record.itemId),
        locationFilter: record.location,
        lineCode: record.lineCode,
        systemCode: Number(record.originalSystemCode || 0),
        partNumber: record.partNumber,
        productRepositoryId: record.eCommerceProductId,
        productId: record.partTerminologyId,
        quickNote: record.quickNote,
        techNote: record.technicalNote,
        recordType: record.recordType,
        skuReviewEnabled: record.reviewEnabledFlag,
        vehicleFitment: record.vehicleFitment?.vehicleFit,
        vehicleFitmentLabel: record.vehicleFitmentLabel,
        warrantyMonths: record.warranty?.warrantyMonths,
        warrantyText: record.warranty?.warrantyText,
        warrantyType: record.warranty?.warrantyType ?? record?.warrantyType,
        brand: record.brandName ?? '',
        name: record.itemDescription,
        productReviewsEnabled: record.reviewEnabledFlag,
        vehicleFit: record.vehicleFitment?.vehicleFit === 'FITS',
        vehicleSpecific: record.vehicleFitmentLabel === 'VEHICLE_SPECIFIC',
        originalPartTypeId: record.originalPartTypeId ?? '',
        // New properties from the ACES API
        subBrandId: record.subBrandId,
        configurableSkuFlag: record.configurableSkuFlag ?? false,
        subBrandName: record.subBrandName,
        partGroupName: record.partGroupName,
        partTerminologyId: record.partTerminologyId,
        partGroupId: record.partGroupId,
        canonicalUrl: record.canonicalUrl,
        categoryIdPath: record.categoryIdPath,
        taxonomyPath: record.taxonomyPath,
        productCanonicalUrl: record.productCanonicalUrl,
        documentId: record.documentId,
        score: record.score,
        perCarQuantity: record.perCarQuantity,
        displayNotesTypes: record.displayNotesTypes,
        availability: record.availability,
        productAttributes: record.productAttributes,
        crossBrand: record.crossBrand,
        crossPartNumber: record.crossPartNumber,
        major: record.major,
        minor: record.minor,
      })) ?? [],
    xFusionQueryId,
  };
};

const getACESBrandProductData = async (
  options: GetProductBrandDataOptionsAces,
  axiosInstance?: AxiosInstance
) => {
  const { locale, ...rest } = options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mapFacet: _mapFacet, ...restWithoutMapFacet } = rest;
  const params: GetBrandListResultsUsingGetParamsModel = {
    ...restWithoutMapFacet,
    country,
    customerType,
    salesChannel,
  };
  const response = await getAxios(axiosInstance).get<BrandShelfAcesResponseModel>(
    ACES_PART_TYPES_URL,

    {
      withCredentials: false,
      params,
    }
  );

  return response.data;
};

const {
  useData: useACESBrandProducts,
  prefetch: prefetchACESProductBrand,
  query: acesProductBrandQuery,
} = createQuery<BrandShelfAcesResponseModel, GetProductBrandDataOptionsAces>(
  'acesBrandProducts',
  getACESBrandProductData
);

export const useACESBrandProductData = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const locale = useLocale();
  const router = useRouter();
  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const preferredVehicle = usePreferredVehicle();
  const { data: storeDetailsData } = useStoreDetailsData();
  const is24ProductViewEnabled = useIs24ProductViewEnabled();
  const storeNumber = storeDetailsData?.storeNumber;
  const vehicleId = preferredVehicle?.catalogVehicleId;
  const isLoadMoreEnabled = useMonetateDecisionFlag('loadMoreEnabled');
  const {
    currentPage,
    sort = [],
    recsPerPage,
    pageNumber,
    facet,
    minPrice,
    maxPrice,
  } = router.query;
  const sortArr = typeof sort === 'string' ? sort.split('-') : sort;
  const { data: headerData } = useHeaderData();
  const sortOrder = sortArr[sortArr.length - 1];
  const sortFieldName = sortArr.slice(0, sortArr.length - 1).join('-');
  const vehicleQuestions = useMemo(
    () =>
      extractVehicleQuestions(
        headerData?.vehicleMap.acesVehicleAttributes?.vehicleQuestionsIds,
        true
      ) as string,
    [headerData?.vehicleMap]
  );
  const mapFacet = useMapFacet();
  const path = router.asPath;
  const parsedUrl = parseUrl(path);
  const options: GetProductBrandDataOptionsAces = {
    locale,
    pageNumber:
      typeof currentPage === 'string'
        ? Number(currentPage)
        : typeof pageNumber === 'string'
        ? Number(pageNumber)
        : 1,
    vehicleId,
    recordsPerPage:
      typeof recsPerPage === 'string'
        ? Number(recsPerPage)
        : is24ProductViewEnabled
        ? 24
        : undefined,
    minPrice: minPrice ? String(minPrice) : undefined,
    maxPrice: maxPrice ? String(maxPrice) : undefined,
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    facet: Array.isArray(facet) ? facet.join(',') : facet,
    mapFacet: mapFacet,
    sort: sortFieldName && sortOrder ? `${sortFieldName}-${sortOrder}` : undefined,
    storeId: storeNumber,
    ...(!!vehicleQuestions && { vehicleQuestions }),
    seoUrl: parsedUrl.url,
    preview: showXMPreviewDate(),
  };
  let infiniteQueryData: InfiniteData<BrandShelfAcesResponseModel> | undefined;
  let fetchNextPageBrandProductData = () => Promise.resolve();
  let hasNextPageBrandProductData: boolean | undefined = false;
  let fetchPreviousPageBrandProductData = () => Promise.resolve();
  let hasPreviousPageBrandProductData: boolean | undefined = false;
  let statusBrandProductData: QueryStatus | 'idle' = 'idle';
  let refetchBrandProductResults = () => Promise.resolve();

  const infiniteQueryResult = useInfiniteQuery({
    queryKey: [...acesProductBrandQuery.getFullKey(options), 'infinite'],
    queryFn: ({ pageParam }) =>
      getACESBrandProductData({
        ...options,
        pageNumber: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage?.brandShelfResults?.totalNumberOfRecords ?? 0;
      const recsPerPage = Number(options.recordsPerPage) || 24;
      const currentPageNumber = allPages.length;
      const totalPages = Math.ceil(totalRecords / recsPerPage);

      if (currentPageNumber < totalPages) {
        return currentPageNumber + 1;
      }
      return undefined;
    },
    enabled: enabled && acesEnabled && acesNonVehicleEndpointsEnabled && isLoadMoreEnabled,
  });

  useQueryResultOnSuccess(infiniteQueryResult, async (data) => {
    const lastPage = data.pages[data.pages.length - 1];
    if (lastPage.redirectUrl) {
      await router.replace(lastPage.redirectUrl);
    }
  });

  const regularQueryResult = useACESBrandProducts({
    ...options,
    enabled: enabled && acesEnabled && acesNonVehicleEndpointsEnabled && !isLoadMoreEnabled,
  });

  useQueryResultOnSuccess(regularQueryResult, async (data) => {
    if (data.redirectUrl) {
      await router.replace(data.redirectUrl);
    }
  });

  if (isLoadMoreEnabled) {
    const {
      data,
      fetchNextPage,
      hasNextPage,
      fetchPreviousPage,
      hasPreviousPage,
      status,
      refetch,
    } = infiniteQueryResult;
    fetchNextPageBrandProductData = () => fetchNextPage().then(() => {});
    hasNextPageBrandProductData = hasNextPage;
    fetchPreviousPageBrandProductData = () => fetchPreviousPage().then(() => {});
    hasPreviousPageBrandProductData = hasPreviousPage;
    statusBrandProductData = status;
    refetchBrandProductResults = () => refetch().then(() => {});
    infiniteQueryData = data;
  }
  const mergedData = useMemo(() => {
    if (!infiniteQueryData?.pages) {
      return undefined;
    }

    const allSkuRecords: SkuRecordModel[] = [];
    let lastBrandShelfResults: BrandShelfAcesResponseModel['brandShelfResults'] | undefined;
    let lastXFusionQueryId: string | undefined;
    let lastRedirectUrl: string | undefined;
    let lastRedirectPageType: string | undefined;

    infiniteQueryData.pages.forEach((page) => {
      if (page?.brandShelfResults?.skuRecords) {
        allSkuRecords.push(...(page.brandShelfResults.skuRecords as SkuRecordModel[]));
        lastBrandShelfResults = page.brandShelfResults;
        lastXFusionQueryId = page.xFusionQueryId;
        lastRedirectUrl = page.redirectUrl;
        lastRedirectPageType = page.redirectPageType;
      }
    });
    if (!lastBrandShelfResults) {
      return undefined;
    }

    return {
      brandShelfResults: {
        ...lastBrandShelfResults,
        skuRecords: allSkuRecords,
      },
      redirectUrl: lastRedirectUrl,
      redirectPageType: lastRedirectPageType,
      xFusionQueryId: lastXFusionQueryId,
    };
  }, [infiniteQueryData?.pages]);
  const isFetching = isLoadMoreEnabled
    ? infiniteQueryResult.isFetching
    : regularQueryResult.isFetching;

  const isPending = isLoadMoreEnabled
    ? infiniteQueryResult.isPending
    : regularQueryResult.isPending;
  const resultData = isLoadMoreEnabled ? mergedData : regularQueryResult.data;

  const selectedData = selector(
    resultData ??
      ({ brandShelfResults: { skuRecords: [] } } as unknown as BrandShelfAcesResponseModel),
    mapFacet ?? ((facet: FacetModel) => getMapFacet(facet, 'Clearance'))
  );

  return {
    data: selectedData,
    pageNumber: isLoadMoreEnabled ? infiniteQueryData?.pages.length ?? 1 : options.pageNumber,
    fetchNextPage: fetchNextPageBrandProductData,
    hasNextPage: hasNextPageBrandProductData,
    fetchPreviousPage: fetchPreviousPageBrandProductData,
    hasPreviousPage: hasPreviousPageBrandProductData,
    isFetching,
    isPending,
    status: isLoadMoreEnabled ? statusBrandProductData : regularQueryResult.status,
    refetch: isLoadMoreEnabled ? refetchBrandProductResults : regularQueryResult.refetch,
    fetchStatus: isLoadMoreEnabled
      ? infiniteQueryResult.fetchStatus
      : regularQueryResult.fetchStatus,
    isSuccess: isLoadMoreEnabled ? infiniteQueryResult.isSuccess : regularQueryResult.isSuccess,
    isError: isLoadMoreEnabled ? infiniteQueryResult.isError : regularQueryResult.isError,
  };
};

export const prefetchACESBrandInfinityData = (
  queryClient: QueryClient,
  options: GetProductBrandDataOptionsAces,
  axiosInstance?: AxiosInstance
) => {
  return queryClient.prefetchInfiniteQuery({
    queryKey: [...acesProductBrandQuery.getFullKey(options), 'infinite'],
    queryFn: ({ pageParam }) =>
      getACESBrandProductData({ ...options, pageNumber: pageParam }, axiosInstance),
    initialPageParam: 1,
  });
};

export const getBrandProductDataFromCache = (
  queryClient: QueryClient,
  options: GetProductBrandDataOptionsAces
): BrandShelfAcesResponseModel | undefined => {
  const isLoadMoreEnabled = getKiboDecisionFlag(queryClient, 'loadMoreEnabled');
  if (isLoadMoreEnabled) {
    return queryClient.getQueryData<InfiniteData<BrandShelfAcesResponseModel>>([
      ...acesProductBrandQuery.getFullKey(options),
      'infinite',
    ])?.pages?.[0];
  }

  return queryClient.getQueryData<BrandShelfAcesResponseModel>(
    acesProductBrandQuery.getFullKey(options)
  );
};

export { prefetchACESProductBrand };
